import { Link } from "react-router-dom";
import { Heart, Users, Building2, Gift, Star, Quote } from "lucide-react";

const STORY = [
  "LP Foundation started with a simple purpose. As a parent, I noticed my son loved music, video games, and technology — but struggled to connect with traditional schoolwork. I realized he wasn't disengaged from learning. He was disengaged from how it was being delivered.",
  "Instead of separating academics from his interests, I combined them. We began embedding academic skills into the things he already cared about — turning passion into performance and interest into structured growth. The change was immediate. Focus improved. Confidence increased. Learning became relevant.",
  "What began as a solution for my own child quickly grew into something larger. Other families saw the impact and wanted the same structured approach. From there, LP Foundation expanded into a community-based organization delivering hands-on programs across multiple sites — partnering with the Atlanta Public Library, Microsoft, Best Buy, the YMCA, Boys & Girls Club, and local community centers.",
  "Our purpose has remained the same: prepare students to perform — not just participate. Beyond programs, we continue serving families through initiatives like Feed the Family and our annual Secret Santa support for underserved children. LP Foundation exists because learning should connect to life — and structure should unlock potential.",
];

const IMPACT = [
  { icon: Users, stat: "1,000+", label: "Students impacted through programs, workshops, and summer camps." },
  { icon: Building2, stat: "Multi-site", label: "Partnerships with Atlanta Public Library, Microsoft, Best Buy, the YMCA, Boys & Girls Club, and community & recreation centers." },
  { icon: Gift, stat: "Year-round", label: "Community outreach via Feed the Family and Secret Santa initiatives." },
  { icon: Star, stat: "Proven", label: "Engaging, innovative programs that inspire and educate." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">About LP Foundation</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            A community-driven youth development organization.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            We prepare the next generation for opportunity. We believe students perform best when they are prepared —
            mentally, emotionally, and academically. We are built on trust, structure, and measurable growth.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12">
        <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
          <h2 className="font-display text-2xl font-black text-brandnavy">Who We Are</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            LP Foundation is a community-driven youth development organization focused on preparing the next generation for
            opportunity. Every program we run begins with performance readiness and continues with structured, hands-on
            learning.
          </p>
        </div>
        <div className="rounded-3xl bg-orange-50 border border-orange-100 p-8">
          <h2 className="font-display text-2xl font-black text-brandnavy">Our Mission</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">
            To prepare students for real-world success through structured, hands-on programs that build performance
            readiness, applied academic skills, and workforce confidence.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <h2 className="font-display text-3xl font-black text-brandnavy text-center">Our Story</h2>
          <div className="mt-10 space-y-6">
            {STORY.map((p, i) => (
              <p key={i} className="text-lg text-slate-600 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <h2 className="font-display text-3xl font-black text-brandnavy text-center">Our Impact</h2>
        <p className="mt-3 text-center text-slate-600">Making a difference every day.</p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {IMPACT.map((i) => (
            <div key={i.stat} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm text-center">
              <i.icon className="mx-auto h-8 w-8 text-accent" />
              <div className="mt-4 font-display text-2xl font-black text-brandnavy">{i.stat}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{i.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Community Outreach</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">
              Feed the Family
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Beyond our youth programs, LP Foundation shows up for the whole community. Through{" "}
              <span className="font-semibold text-brandnavy">Feed the Family</span>, our team and volunteers prepare and
              serve hot meals to neighbors experiencing homelessness and food insecurity — with dignity, warmth, and care.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              It's part of who we are: meeting people where they are, strengthening our community, and showing the next
              generation what it means to give back. We also bring joy each year through our{" "}
              <span className="font-semibold text-brandnavy">Secret Santa</span> initiative for underserved children.
            </p>
            <Link
              to="/get-involved"
              data-testid="about-volunteer-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brandnavy px-7 py-3.5 font-semibold text-white transition hover:-translate-y-0.5"
            >
              <Heart className="h-4 w-4 fill-current" /> Volunteer With Us
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="/img/feed1_web.jpg"
              alt="LP Foundation Feed the Family — serving hot meals to the community"
              className="h-56 sm:h-72 w-full rounded-3xl object-cover shadow-md"
              loading="lazy"
            />
            <img
              src="/img/feed2_web.jpg"
              alt="LP Foundation Feed the Family outreach event"
              className="h-56 sm:h-72 w-full rounded-3xl object-cover shadow-md mt-8"
              loading="lazy"
            />
          </div>
        </div>
      </section>


      <section className="mx-auto max-w-4xl px-5 sm:px-8 pb-20">
        <div className="rounded-3xl bg-brandnavy p-10 text-center">
          <Quote className="mx-auto h-10 w-10 text-accent" />
          <blockquote className="mt-4 font-display text-2xl font-bold text-white leading-snug">
            "LP Foundation helped my son gain confidence and focus. He's excited about learning again."
          </blockquote>
          <p className="mt-4 text-slate-400">— Jennette B., Parent</p>
          <Link to="/support" data-testid="about-donate-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-orange-400">
            <Heart className="h-4 w-4 fill-current" /> Support Our Mission
          </Link>
        </div>
      </section>
    </div>
  );
}
